import * as THREE from "three";
import {
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector3,
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { ACTION_TRIGGERS } from "../ActionTriggers";
import { useStore } from "../store/Store";
import { onBeforeCompileBloom } from "../utils/bloomHelper";

/**
 * This is the pipeline for face masks.
 * For now NO FACE-PAINT (tatoo/makeup) supported.
 * Per default an occluder is loaded and place onto the head.
 * 3D models are hidden as soon as face is lost and shown as soon as face is found.
 * Also, there are events fired for face found and face lost.
 *
 * @param param0
 * @returns
 */

export const three8thFacePipeline = ({
  camera,
  scene,
  renderer,
  onVideoReady,
  onTrackingReady,
  onFaceFound,
  onFaceLost,
}: {
  xr8: any;
  camera: THREE.PerspectiveCamera;
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;

  onTrackingReady?: () => void;
  onVideoReady?: () => void;
  onFaceFound: (detail: any) => void;
  onFaceLost: () => void;
}) => {
  console.log("createThree8thFacePipeline");

  let trackingGroup: THREE.Scene;

  let hasVideoDone = false;
  let _headObject: Object3D;
  let _mModelGeom: any;
  let _canvas: HTMLCanvasElement;
  let _headMesh: ReturnType<typeof faceMesh>;

  // Update geometry on each frame with new info from the face controller.
  const show = ({ detail }: { detail: any }) => {
    if (_headMesh) _headMesh.show(detail);

    if (!(trackingGroup && _headObject)) return;
    const { transform } = detail;

    _headObject.position.copy(transform.position as Vector3);
    _headObject.setRotationFromQuaternion(transform.rotation as Quaternion);
    _headObject.scale.set(transform.scale, transform.scale, transform.scale);

    _headObject.visible = true;
    onFaceFound(detail);
  };

  const faceMesh = (modelGeometry: any, material: Material) => {
    const geometry = new THREE.BufferGeometry();

    // Fill geometry with default vertices.
    const vertices = new Float32Array(modelGeometry.pointsPerDetection * 3);
    geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));

    // Fill geometry with default normals.
    const normals = new Float32Array(modelGeometry.pointsPerDetection * 3);
    geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));

    // Add the UVs to the geometry.
    const uvs = new Float32Array(modelGeometry.uvs.length * 2);
    for (let i = 0; i < modelGeometry.uvs.length; ++i) {
      uvs[i * 2] = modelGeometry.uvs[i].u;
      uvs[i * 2 + 1] = modelGeometry.uvs[i].v;
    }
    geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));

    // Add the indices.
    const indices = new Array(modelGeometry.indices.length * 3);
    for (let i = 0; i < modelGeometry.indices.length; ++i) {
      indices[i * 3] = modelGeometry.indices[i].a;
      indices[i * 3 + 1] = modelGeometry.indices[i].b;
      indices[i * 3 + 2] = modelGeometry.indices[i].c;
    }
    geometry.setIndex(indices);

    const mesh = new THREE.Mesh(geometry, material);

    const show = (detail: any) => {
      // Update vertex positions.
      for (let i = 0; i < detail.vertices.length; ++i) {
        vertices[i * 3] = detail.vertices[i].x;
        vertices[i * 3 + 1] = detail.vertices[i].y;
        vertices[i * 3 + 2] = detail.vertices[i].z;
      }
      mesh.geometry.attributes.position.needsUpdate = true;

      // Update vertex normals.
      for (let i = 0; i < detail.normals.length; ++i) {
        normals[i * 3] = detail.normals[i].x;
        normals[i * 3 + 1] = detail.normals[i].y;
        normals[i * 3 + 2] = detail.normals[i].z;
      }
      mesh.geometry.attributes.normal.needsUpdate = true;

      mesh.visible = true;
    };

    const hide = () => {
      mesh.visible = false;
    };

    return {
      mesh,
      show,
      hide,
    };
  };

  // Builds the 3d head mesh
  const buildHeadMesh = (modelGeometry: any) => {
    const material = new MeshStandardMaterial({
      roughness: 0,
      color: 0x000000ff,
      transparent: true,
      side: THREE.DoubleSide,
      wireframe: true,
      opacity: 0,
    });
    // because there is a chance that we have bloom in the scene
    // we make sure the generated face mesh can't glow
    // material.onBeforeCompile((shader) => {});
    const headMesh = faceMesh(modelGeometry, material);
    useStore.getState().addAsset(headMesh.mesh, "8thWallFaceMesh");
    useStore
      .getState()
      .triggerAction(ACTION_TRIGGERS.MODEL_READY, "8thWallFaceMesh");
    return headMesh;
  };

  const buildHead = (_modelGeometry: any) => {
    const headObject = new Object3D();
    headObject.name = "headObject";
    headObject.visible = false;

    // TODO: This is needed for tatoo effects
    const headMesh = buildHeadMesh(_modelGeometry);
    headMesh.mesh.name = "facePaintMesh";
    headObject.add(headMesh.mesh);
    _headMesh = headMesh;

    const loader = new GLTFLoader();

    // Add occluder.
    loader.load(
      "https://storage.googleapis.com/webar.xrii.io/utils/head-occluder-2.glb",
      (occluder) => {
        occluder.scene.scale.set(1.1, 1.1, 1.4);
        occluder.scene.position.set(-0.02, 0, 0.25);
        occluder.scene.traverse((node) => {
          if ((node as Mesh).isMesh) {
            const mat = new THREE.MeshStandardMaterial();
            mat.colorWrite = false;
            mat.onBeforeCompile = onBeforeCompileBloom;
            node.name = "occluder";
            node.renderOrder = -1;
            node.castShadow = false;
            node.receiveShadow = false;
            (node as Mesh).material = mat;
          }
        });
        console.log("occluder", occluder);
        headObject.add(occluder.scene);
      }
    );
    return headObject;
  };

  // init is called by onAttach and by facecontroller.faceloading. It needs to be called by both
  // before we can start.
  const init = ({
    canvas,
    detail,
  }: {
    canvas: HTMLCanvasElement;
    detail: any;
  }) => {
    renderer.autoClearColor = false;

    if (_headObject) {
      return;
    }
    _mModelGeom = _mModelGeom || detail;
    _canvas = _canvas || canvas;
    if (!(_canvas && _mModelGeom)) {
      return;
    }
    // renderer.setSize(_canvas.width, _canvas.height);
    camera.position.set(0, 0, 0);
    camera.matrixWorldNeedsUpdate = true;
    camera.updateProjectionMatrix();

    // _headObject is anchored to the real world head.
    _headObject = buildHead(_mModelGeom);
    scene.add(_headObject);
    // prevent scroll/pinch gestures on canvas.
    // canvas_.addEventListener('touchmove', (event) => event.preventDefault())
  };

  const hide = () => {
    if (_headMesh) _headMesh.hide();

    if (!_headObject) return;
    _headObject.visible = false;
    onFaceLost();
  };
  let trackingDone = false;

  return {
    name: "facePipeline",
    onException: (err: any) => console.error(err),
    onCanvasSizeChange: ({
      canvasWidth,
      canvasHeight,
    }: {
      canvasWidth: number;
      canvasHeight: number;
    }) => {
      if (
        renderer.domElement.width !== canvasWidth &&
        renderer.domElement.height !== canvasHeight
      ) {
        console.log("onCanvasSizeChange", canvasWidth, canvasHeight);
        renderer?.setSize(canvasWidth, canvasHeight);
        camera.aspect = canvasWidth / canvasHeight;
        camera.updateProjectionMatrix();
      }
    },
    onUpdate: (event: any) => {
      if (event.processCpuResult.facecontroller) {
        const { intrinsics } = event.processCpuResult.facecontroller;
        for (let i = 0; i < 16; i += 1) {
          camera.projectionMatrix.elements[i] = intrinsics[i];
        }
        camera.projectionMatrixInverse.copy(camera.projectionMatrix);
        camera.projectionMatrixInverse.invert();

        camera.matrixWorldNeedsUpdate = true;
      }

      if (!trackingDone) {
        trackingDone = true;
        onTrackingReady?.();
      }
      if (!trackingGroup) {
        scene.traverse((o: Object3D) => {
          if (o.name === "trackingGroup") {
            trackingGroup = o as THREE.Scene;
          }
        });
        if (trackingGroup) {
          _headObject.add(trackingGroup);
          scene.remove(trackingGroup);
        }
      }
    },
    onCameraStatusChange: ({ status }: any) => {
      if (status === "hasVideo" && !hasVideoDone) {
        hasVideoDone = true;
        onVideoReady?.();
      }
      if (status === "failed") {
        console.error(new Error("camera permission failed"));
      }
    },

    onAttach: init,
    onDetatch: undefined, //TODO,
    onRender: () => {
      if (!renderer) {
        return;
      }
      if (scene) {
        renderer.render(scene, camera);
      }
    },
    listeners: [
      { event: "facecontroller.faceloading", process: init },
      { event: "facecontroller.facefound", process: show },
      { event: "facecontroller.faceupdated", process: show },
      { event: "facecontroller.facelost", process: hide },
    ],
  };
};
